import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { searchChatMessages } from '../utils/chatApi';
import { ChatSearchResult } from '../types';
import { buildCsvContent, downloadCsv } from '../ChatDashboard/utils/exportUtils';

interface ChatSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChatSearchDialog: React.FC<ChatSearchDialogProps> = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeKeyword, setActiveKeyword] = useState<string>('');
  const normalizedKeyword = useMemo(() => activeKeyword.trim(), [activeKeyword]);
  const [results, setResults] = useState<ChatSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ChatSearchResult | null>(null);
  const [totalFound, setTotalFound] = useState<number | null>(null);

  const canExportResults = normalizedKeyword.length > 0 && results.length > 0 && !loading && !error;

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setActiveKeyword('');
      setResults([]);
      setLoading(false);
      setError(null);
      setSelectedMessage(null);
      setTotalFound(null);
    }
  }, [open]);

  const executeSearch = useCallback(
    async (keyword: string) => {
      const trimmed = keyword.trim();
      if (!trimmed) {
        setResults([]);
        setError('Please enter a keyword to search.');
        setLoading(false);
        setSelectedMessage(null);
        setTotalFound(null);
        return;
      }

      setLoading(true);
      setError(null);
      setSelectedMessage(null);
      setTotalFound(null);

      try {
        const response = await searchChatMessages(trimmed);
        setResults(response.data?.results ?? []);
        setTotalFound(
          typeof response.data?.total_found === 'number' ? response.data.total_found : null
        );
      } catch (err) {
        console.error('Failed to search chat messages:', err);
        setResults([]);
        setTotalFound(null);
        setError(
          err instanceof Error ? err.message || 'Failed to fetch search results.' : 'Failed to fetch search results.'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (open && normalizedKeyword) {
      executeSearch(normalizedKeyword);
    }
  }, [open, normalizedKeyword, executeSearch]);

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    if (trimmed) {
      setActiveKeyword(trimmed);
    } else {
      setActiveKeyword('');
      setResults([]);
      setSelectedMessage(null);
      setTotalFound(null);
      setError('Please enter a keyword to search.');
    }
  };

  const handleExport = () => {
    if (!canExportResults) {
      return;
    }
    const csv = buildCsvContent(
      ['Keyword', 'Message ID', 'Question', 'Answer', 'Question Time', 'Answer Time'],
      results.map((result) => [
        normalizedKeyword,
        result.message_id || '',
        result.question || '',
        result.answer || '',
        result.question_timestamp || '',
        result.answer_timestamp || '',
      ])
    );
    const safeKeyword = normalizedKeyword.replace(/\s+/g, '_');
    downloadCsv(csv, `chat_search_${safeKeyword || 'keyword'}.csv`);
  };

  const renderResults = () => {
    if (!normalizedKeyword) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Enter a keyword to search.
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          Searching messages...
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
          {error}
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-gray-400">
          No messages match “{normalizedKeyword}”.
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-[300px] flex-col">
        <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <span className="flex-1">Question</span>
          <span className="w-36 text-right">Question Time</span>
        </div>
        <div
          className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}
        >
          {results.map((row) => (
            <div
              key={row.message_id}
              className="border-t border-gray-100 px-1 py-3 text-sm text-gray-700"
            >
              <div className="flex items-start gap-4">
                <Tooltip title={`Message ID: ${row.message_id}`} arrow>
                  <button
                    type="button"
                    className="flex-1 text-left font-medium text-gray-900 hover:text-indigo-600"
                    onClick={() => setSelectedMessage(row)}
                  >
                    {row.question || '--'}
                  </button>
                </Tooltip>
                <span className="w-36 text-right text-xs text-gray-500">
                  {formatDateLabel(row.question_timestamp)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ className: 'rounded-2xl' }}>
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <span>{renderDialogTitle(normalizedKeyword, totalFound)}</span>
            <Tooltip title="Export to CSV">
              <span>
                <IconButton size="small" onClick={handleExport} disabled={!canExportResults}>
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Search text in questions and answers..."
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="flex w-full items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
              >
                Search
              </button>
            </div>
            <div>{renderResults()}</div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={Boolean(selectedMessage)}
        onClose={() => setSelectedMessage(null)}
        hideBackdrop
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          pointerEvents: 'none',
        }}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 380 }, pointerEvents: 'auto', px: 0 },
        }}
      >
        {selectedMessage && (
          <DrawerContent message={selectedMessage} onClose={() => setSelectedMessage(null)} />
        )}
      </Drawer>
    </>
  );
};

export default ChatSearchDialog;

const DrawerContent: React.FC<{
  message: ChatSearchResult;
  onClose: () => void;
}> = ({ message, onClose }) => (
  <div className="flex h-full w-[360px] flex-col">
    <div className="flex items-center justify-between border-b border-gray-200 pl-4 pr-[6px] py-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Message Detail</p>
      </div>
      <IconButton size="small" onClick={onClose} sx={{ mr: -0.5 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
    <div className="flex-1 overflow-y-auto px-4 py-4 text-sm text-gray-700">
      <InfoSection title="Question" body={message.question || '--'} highlight />
      <InfoSection title="Answer" body={message.answer || 'No answer available.'} allowPreWrap />
      <InfoSection title="Message ID" body={message.message_id || '--'} allowBreak />
      <InfoSection title="Question Time" body={formatDateTimeLabel(message.question_timestamp)} />
      <InfoSection title="Answer Time" body={formatDateTimeLabel(message.answer_timestamp)} />
    </div>
  </div>
);

const InfoSection: React.FC<{
  title: string;
  body: string;
  highlight?: boolean;
  allowPreWrap?: boolean;
  allowBreak?: boolean;
}> = ({ title, body, highlight = false, allowPreWrap = false, allowBreak = false }) => (
  <div className="border-t border-blue-100 first:border-t-0 first:pt-0 first:mt-0 pt-3 mt-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">{title}</p>
    <p
      className={`mt-1 text-sm ${highlight ? 'text-gray-900' : 'text-gray-700'} ${
        allowPreWrap ? 'whitespace-pre-wrap' : ''
      } ${allowBreak ? 'break-all' : ''}`}
    >
      {body}
    </p>
  </div>
);

const formatDateLabel = (timestamp?: string | null): string => {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toISOString().slice(0, 10);
};

const formatDateTimeLabel = (timestamp?: string | null): string => {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const renderDialogTitle = (keyword: string, totalFound: number | null): string => {
  if (!keyword) {
    return 'Search Messages';
  }
  const countLabel = totalFound !== null ? totalFound : 0;
  return `${countLabel} messages related to "${keyword}"`;
};
